export type B2CCheckoutPricing = {
	startFee: number;
	hourlyRate: number;
	dailyCeiling: number;
	weekCeiling: number;
};

export type B2CPricingEstimates = {
	oneHour: unknown;
	twoHours: unknown;
	oneDay: unknown;
	oneWeek: unknown;
};

export function getB2CPricingEstimateDates(now = Date.now()) {
	const hourMs = 60 * 60 * 1000;

	return {
		oneHour: new Date(now + hourMs).toISOString(),
		twoHours: new Date(now + 2 * hourMs).toISOString(),
		oneDay: new Date(now + 24 * hourMs).toISOString(),
		oneWeek: new Date(now + 7 * 24 * hourMs).toISOString(),
	};
}

export function calculateB2CCheckoutPricing(category: string, estimates: B2CPricingEstimates): B2CCheckoutPricing {
	const oneHour = getCategoryEstimate(estimates.oneHour, category);
	const twoHours = getCategoryEstimate(estimates.twoHours, category);
	const oneDay = getCategoryEstimate(estimates.oneDay, category);
	const oneWeek = getCategoryEstimate(estimates.oneWeek, category);
	const hourlyRate = roundPrice(twoHours - oneHour);
	const startFee = roundPrice(oneHour - hourlyRate);

	return {
		startFee,
		hourlyRate,
		dailyCeiling: roundPrice(oneDay - startFee),
		weekCeiling: roundPrice(oneWeek - startFee),
	};
}

/** Cheapest hourly rate across all compartment categories, or null if the estimates are unusable. */
export function lowestB2CHourlyRate(estimates: Pick<B2CPricingEstimates, "oneHour" | "twoHours">): number | null {
	if (!estimates.oneHour || typeof estimates.oneHour !== "object") return null;
	const rates = Object.keys(estimates.oneHour).flatMap((category) => {
		try {
			return [roundPrice(getCategoryEstimate(estimates.twoHours, category) - getCategoryEstimate(estimates.oneHour, category))];
		} catch {
			return [];
		}
	});
	return rates.length > 0 ? Math.min(...rates) : null;
}

export function formatB2CPrice(value: number) {
	return new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR" }).format(value);
}

function getCategoryEstimate(estimate: unknown, category: string) {
	if (!estimate || typeof estimate !== "object") throw new Error("Invalid B2C pricing estimate");

	const value = Number((estimate as Record<string, unknown>)[category]);
	if (!Number.isFinite(value)) throw new Error(`Missing B2C pricing estimate for category ${category}`);
	return value;
}

function roundPrice(value: number) {
	return Math.round(value * 100) / 100;
}
